import path from "node:path";
import type { InstallManifest } from "@skaile/workspaces/types";
import { Command } from "commander";
import { colorRef, kindColorPad, logErr, logInfo, logOk, S } from "../helpers.ts";
import { catalogAssetRef, filterCatalogAssets } from "./catalog.ts";
import {
  deviceLogin,
  hasStoredAuth,
  loadStoreAuthConfig,
  logout as storeLogout,
} from "../store-auth.ts";
import {
  getStoreConfig,
  getStoreToken,
  isStoreAuthenticated,
  storeFetch,
} from "../store-client.ts";

/** Fetch a bearer token + call `/api/v1/me`, returning the publisher identity. */
async function fetchIdentity(): Promise<{
  username?: string;
  githubLogin?: string | null;
  roles?: string[];
}> {
  const jwt = await getStoreToken();
  return storeFetch({ ...getStoreConfig(), jwt }, "/api/v1/me");
}

/**
 * Creates the `skaile store` command group.
 *
 * Manages authentication and publishing operations against the AI Asset Store.
 * Subcommands: `login` (GitHub OAuth or email/password), `logout`, `whoami`,
 * `publish` (push a new asset version), `yank` (soft-delete a version).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/store#make-store-command
 */
export function makeStoreCommand(): Command {
  const store = new Command("store").description("Manage the AI Asset Store");

  // ── login ──
  store
    .command("login")
    .description("Sign in to the store (Keycloak device flow — approve in your browser)")
    .action(async () => {
      try {
        await deviceLogin(loadStoreAuthConfig());
        const me = await fetchIdentity().catch(
          () => ({}) as { username?: string; githubLogin?: string | null },
        );
        // The login name is NOT a namespace (no `@`). The publishable namespace is
        // the connected GitHub handle — show it separately so `@<login>` (which may
        // be reserved) is never mistaken for where you publish.
        logOk(
          `Signed in${
            me.username
              ? ` as ${S.brand(me.username)}${me.githubLogin ? ` — publishing as ${S.brand(`@${me.githubLogin}`)}` : ""}`
              : "."
          }`,
        );
      } catch (e: any) {
        logErr(e.message);
        process.exit(1);
      }
    });

  // ── logout ──
  store
    .command("logout")
    .description("Sign out of the store")
    .action(() => {
      storeLogout(loadStoreAuthConfig());
      logOk("Signed out.");
    });

  // ── whoami ──
  store
    .command("whoami")
    .description("Show current store identity")
    .action(async () => {
      if (!hasStoredAuth(loadStoreAuthConfig())) {
        logErr("Not signed in. Run: skaile store login");
        process.exit(1);
      }
      try {
        const me = await fetchIdentity();
        console.log();
        // Login name (not a namespace) vs the publishable namespace (GitHub handle).
        console.log(`  ${S.label("Login")}      ${S.brand(me.username ?? "?")}`);
        console.log(
          `  ${S.label("Publisher")}  ${
            me.githubLogin
              ? S.brand(`@${me.githubLogin}`)
              : S.dim("— connect GitHub in the store to publish")
          }`,
        );
        if (me.roles?.length) console.log(`  ${S.label("Roles")}      ${me.roles.join(", ")}`);
        console.log();
      } catch (e: any) {
        logErr(e.message);
        process.exit(1);
      }
    });

  // ── mode ──
  store
    .command("mode <auto|local|remote>")
    .description("Set the catalog resolution mode (not yet wired)")
    .action((_mode: string) => {
      logErr(
        "`skaile store mode` is scaffolded but not yet wired. " +
          "Store config lives at ~/.skaile/store/config.yaml.",
      );
      process.exit(2);
    });

  // ── search ──
  store
    .command("search <query>")
    .description("Search the AI Asset Store catalog by name, description, or publisher")
    .option("--kind <kind>", "Filter by asset kind (skill, agent, prompt, flow, …)")
    .option("-C, --project-dir <dir>", "Project directory (catalog config overlay)", process.cwd())
    .action(async (query: string, opts) => {
      const { openCatalogSource, LocalCatalogDisabledError } = await import("../open-library.ts");
      try {
        const remote = await openCatalogSource({ projectDir: path.resolve(opts.projectDir) });
        // The remote catalog has no server-side query on `listAssets` (the `kind`
        // filter IS server-side); text matching is client-side, same as
        // `skaile search --store`.
        const assets = await remote.listAssets(opts.kind ? { kind: opts.kind } : {});
        const results = filterCatalogAssets(assets, query);
        if (results.length === 0) {
          logInfo(`No store results for "${query}".`);
          return;
        }
        console.log();
        for (const a of results) {
          const ref = catalogAssetRef(a);
          console.log(
            `  ⬡ ${kindColorPad(a.kind)}  ${colorRef(ref).padEnd(48)}  ${S.dim((a.description ?? "").slice(0, 60))}`,
          );
        }
        console.log(
          `\n  ${S.dim(`${results.length} result(s) · \`skaile store show <ref>\` for details`)}\n`,
        );
      } catch (e: any) {
        if (e instanceof LocalCatalogDisabledError) {
          logErr(
            "Remote catalog disabled (catalog.url: local). Set `catalog.url: https://skaile.store` in ~/.skaile/config.yaml.",
          );
        } else {
          logErr(`Store search failed: ${e.message}`);
        }
        process.exit(1);
      }
    });

  // ── show ──
  store
    .command("show <ref>")
    .description("Show a store asset's details + provenance (source, files, integrity)")
    .option("-C, --project-dir <dir>", "Project directory (catalog config overlay)", process.cwd())
    .action(async (ref: string, opts) => {
      const { openCatalogSource, LocalCatalogDisabledError } = await import("../open-library.ts");
      const { supportsInstallManifest } = await import("./project.ts");
      try {
        const remote = await openCatalogSource({ projectDir: path.resolve(opts.projectDir) });
        const asset = await remote.resolve(ref);
        if (!asset) {
          logErr(`Not found in store: ${ref} (try a full ref, e.g. skill:@publisher/name#version)`);
          process.exit(1);
        }
        const canonical = catalogAssetRef(asset);
        // Pointer-triple provenance is served only by the tRPC RemoteCatalogSource;
        // best-effort so a REST-framed or unpublished asset still prints its card.
        let manifest: InstallManifest | null = null;
        if (supportsInstallManifest(remote)) {
          manifest = await remote.getInstallManifest(canonical).catch(() => null);
        }

        const field = (label: string, value: string) => `  ${S.label(label.padEnd(11))}${value}`;
        console.log();
        console.log(
          `  ${kindColorPad(asset.kind)}  ${S.heading(`@${asset.publisher}/${asset.name}`)}  ${S.dim(`v${asset.version}`)}`,
        );
        console.log(`  ${S.rule(52)}`);
        if (asset.description) {
          console.log();
          console.log(`  ${asset.description}`);
        }
        console.log();
        if (manifest) console.log(field("Tier", manifest.tier));
        if (asset.category) console.log(field("Category", asset.category));
        if (asset.license) console.log(field("License", asset.license));

        if (manifest) {
          console.log();
          console.log(`  ${S.heading("Source")}`);
          console.log(field("Repo", manifest.source.url));
          console.log(field("Commit", manifest.source.commitSha));
          console.log();
          console.log(`  ${S.heading(`Files (${manifest.files.length})`)}`);
          for (const f of manifest.files) {
            console.log(`  ${S.dim(f.sha256.slice(0, 12))}  ${f.path}`);
          }
          console.log();
          console.log(field("Integrity", manifest.sha256));
        } else if (asset.repo) {
          console.log(field("Repo", asset.repo));
          if (asset.sha256) console.log(field("Integrity", asset.sha256));
        }

        if (asset.softDeps?.length) {
          console.log();
          console.log(`  ${S.heading("Soft dependencies")}`);
          for (const dep of asset.softDeps) console.log(`  ${S.dim("→")} ${dep}`);
        }

        console.log();
        console.log(`  ${S.dim(`Install:  skaile add ${canonical}`)}`);
        console.log();
      } catch (e: any) {
        if (e instanceof LocalCatalogDisabledError) {
          logErr(
            "Remote catalog disabled (catalog.url: local). Set `catalog.url: https://skaile.store` in ~/.skaile/config.yaml.",
          );
        } else {
          logErr(`Store show failed: ${e.message}`);
        }
        process.exit(1);
      }
    });

  // ── favourite / unfavourite ──
  store
    .command("favourite <ref>")
    .description("Pin a ref to your favourites (not yet wired)")
    .action((_ref: string) => {
      logErr("`skaile store favourite` is scaffolded but not yet wired.");
      process.exit(2);
    });
  store
    .command("unfavourite <ref>")
    .description("Remove a ref from your favourites (not yet wired)")
    .action((_ref: string) => {
      logErr("`skaile store unfavourite` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── sync ──
  store
    .command("sync")
    .description("Refresh the local cache from the remote store (not yet wired)")
    .action(() => {
      logErr("`skaile store sync` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── push ──
  store
    .command("push")
    .description("Push the local store to a git remote (not yet wired)")
    .option("--remote <name>", "Git remote name", "origin")
    .action(() => {
      logErr("`skaile store push` is scaffolded but not yet wired.");
      process.exit(2);
    });

  // ── yank ──
  store
    .command("yank <version>")
    .description("Yank (soft-delete) a published version")
    .option("--asset <ref>", "Asset reference (@publisher/slug)")
    .action(async (version: string, opts) => {
      if (!isStoreAuthenticated()) {
        logErr("Not signed in. Run: skaile store login");
        process.exit(1);
      }

      if (!opts.asset) {
        logErr("--asset is required (e.g. --asset @alice/my-skill)");
        process.exit(1);
      }

      const ref = opts.asset.replace(/^@/, "");
      const [publisher, slug] = ref.split("/");
      if (!publisher || !slug) {
        logErr("Invalid asset ref. Use format: @publisher/slug");
        process.exit(1);
      }

      try {
        // Authorize with a fresh device-flow token (config.jwt is null now).
        const jwt = await getStoreToken();
        await storeFetch(
          { ...getStoreConfig(), jwt },
          `/api/assets/${publisher}/${slug}/${version}/yank`,
          {
            method: "PATCH",
          },
        );
        logOk(`Yanked v${version} of @${publisher}/${slug}`);
      } catch (e: any) {
        logErr(e.message);
        process.exit(1);
      }
    });

  return store;
}
